import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import VehicleTypeSelect from "../../masters/vehicles/components/VehicleTypeSelect";
import { vehicleTypeLabel } from "../../masters/vehicles/vehicleTypes";
import {
  useCreateFuelRefillMutation,
  useGetFuelCardsQuery,
  useGetFuelCardVehiclesQuery,
  useGetFuelVehiclesQuery,
  useUploadFuelRefillAttachmentMutation,
} from "../fuelApi";
import { FUEL_ENTRY_COLORS, PaymentModeBadge, money } from "../fuelFormat";

const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const ATTACHMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];

/**
 * Top-up every card of one vehicle type in one go (2026-09-30, "give an option to
 * refill for all vehicles under the type, so that the user does not have to do it one
 * by one"). The office works from a paper sheet - one line per card with its vehicle,
 * the current balance and the top-up amount - so the modal reproduces that sheet:
 * pick the type, get a row per card that is linked to a vehicle of that type, type an
 * amount on each row (blank = skip), submit.
 *
 * There is no bulk endpoint: each row becomes its own refill through the normal
 * create call, one after the other, so every card still gets its own SUBMITTED row
 * for approval, and the shared document is attached to each of them. A row that
 * fails keeps its amount and shows the reason; the ones that went through are
 * ticked and locked, so "Submit" again only retries what failed.
 *
 * A card is listed ONCE even when several vehicles of the type share it (the amount
 * loads onto the card, not the vehicle), and a vehicle that has more than one card
 * shows one row per card with its default card marked - leave the others blank.
 */
const FuelRefillByTypeModal = ({ show, onClose }) => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const [vehicleType, setVehicleType] = useState("");
  const [amounts, setAmounts] = useState({}); // fuelCardId -> amount text
  const [done, setDone] = useState({}); // fuelCardId -> refillNumber (created in this session)
  const [errors, setErrors] = useState({}); // fuelCardId -> message
  const [refillDate, setRefillDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: vehicles = [], isFetching: loadingVehicles } = useGetFuelVehiclesQuery(
    { branchId: selectedBranchId ?? undefined, vehicleType },
    { skip: !show || !vehicleType }
  );
  // Active cards only (the server refuses a refill on an inactive card anyway).
  const { data: cards = [] } = useGetFuelCardsQuery({ branchId: selectedBranchId ?? undefined }, { skip: !show });
  const { data: cardVehicleLinks = [] } = useGetFuelCardVehiclesQuery({}, { skip: !show });
  const [createRefill] = useCreateFuelRefillMutation();
  const [uploadAttachment] = useUploadFuelRefillAttachmentMutation();

  useEffect(() => {
    if (!show) return;
    setVehicleType("");
    setAmounts({});
    setDone({});
    setErrors({});
    setRefillDate(dayjs().format("YYYY-MM-DD"));
    setReferenceNumber("");
    setRemarks("");
    setFile(null);
    setIsSaving(false);
  }, [show]);

  // Changing the type starts a fresh sheet.
  useEffect(() => {
    setAmounts({});
    setDone({});
    setErrors({});
  }, [vehicleType]);

  // One row per active card linked to a vehicle of the chosen type; the vehicles
  // that share it are listed on the row (default marked). A vehicle with no active
  // card gets a CASH row instead (Batch200, 2026-10-02) - its top-up is cash handed
  // over for that vehicle, record-only, nothing posted to Petty Cash.
  // Row key: "card:<fuelCardId>" or "cash:<vehicleId>".
  const { rows } = useMemo(() => {
    const cardById = new Map(cards.map((c) => [c.fuelCardId, c]));
    const vehicleIds = new Set(vehicles.map((v) => v.vehicleId));
    const byCard = new Map();
    const linkedVehicleIds = new Set();
    cardVehicleLinks
      .filter((l) => vehicleIds.has(l.vehicleId) && cardById.has(l.fuelCardId))
      .forEach((l) => {
        linkedVehicleIds.add(l.vehicleId);
        if (!byCard.has(l.fuelCardId)) byCard.set(l.fuelCardId, { card: cardById.get(l.fuelCardId), vehicles: [] });
        byCard.get(l.fuelCardId).vehicles.push({ regNo: l.regNo, brand: l.brand, model: l.model, isDefault: l.isDefault });
      });
    const list = [...byCard.values()].map((r) => ({
      ...r,
      key: `card:${r.card.fuelCardId}`,
      mode: "CARD",
      vehicles: [...r.vehicles].sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || String(a.regNo).localeCompare(String(b.regNo))),
    }));
    vehicles
      .filter((v) => v.isActive && !linkedVehicleIds.has(v.vehicleId))
      .forEach((v) =>
        list.push({
          key: `cash:${v.vehicleId}`,
          mode: "CASH",
          vehicleId: v.vehicleId,
          branchCode: v.branchCode,
          vehicles: [{ regNo: v.regNo, brand: v.brand, model: v.model, isDefault: true }],
        })
      );
    // Sheet order: by the first (default) registration, so the rows read like the paper.
    list.sort((a, b) => String(a.vehicles[0].regNo).localeCompare(String(b.vehicles[0].regNo)));
    return { rows: list };
  }, [vehicles, cards, cardVehicleLinks]);

  const pendingRows = rows.filter((r) => !done[r.key] && Number(amounts[r.key]) > 0);
  const pendingTotal = pendingRows.reduce((sum, r) => sum + Number(amounts[r.key]), 0);
  const balanceTotal = rows.reduce((sum, r) => sum + Number(r.card?.currentBalance || 0), 0);
  const doneCount = Object.keys(done).length;
  const canSave = pendingRows.length > 0 && !isSaving;

  const setAmount = (key, value) => {
    setAmounts((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleFile = (e) => {
    const picked = e.target.files?.[0] || null;
    if (picked && !ATTACHMENT_TYPES.includes(picked.type)) {
      toast.error("The document must be a JPEG, PNG or PDF");
      e.target.value = "";
      return;
    }
    if (picked && picked.size > MAX_ATTACHMENT_BYTES) {
      toast.error("The document must be 2 MB or smaller");
      e.target.value = "";
      return;
    }
    setFile(picked);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const newDone = {};
    const newErrors = {};
    let uploadFailures = 0;
    let submittedTotal = 0;
    for (const row of pendingRows) {
      const key = row.key;
      const amount = Number(amounts[key]);
      let created;
      try {
        // eslint-disable-next-line no-await-in-loop
        created = await createRefill({
          paymentMode: row.mode,
          fuelCardId: row.mode === "CARD" ? row.card.fuelCardId : null,
          vehicleId: row.mode === "CASH" ? row.vehicleId : null,
          refillAmount: amount,
          refillDate: refillDate || null,
          referenceNumber: referenceNumber.trim() || null,
          remarks: remarks.trim() || null,
        }).unwrap();
      } catch (error) {
        newErrors[key] = error?.message || "Could not submit";
        continue;
      }
      newDone[key] = created?.refillNumber || "submitted";
      submittedTotal += amount;
      if (file && created?.recordGuid) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await uploadAttachment({ recordGuid: created.recordGuid, file }).unwrap();
        } catch {
          uploadFailures += 1;
        }
      }
    }
    setDone((prev) => ({ ...prev, ...newDone }));
    setErrors(newErrors);
    setIsSaving(false);

    const okCount = Object.keys(newDone).length;
    const failCount = Object.keys(newErrors).length;
    if (okCount > 0) {
      toast.success(`${okCount} refill${okCount === 1 ? "" : "s"} submitted for approval - ${money(submittedTotal)}`);
    }
    if (uploadFailures > 0) {
      toast.error(`${uploadFailures} refill${uploadFailures === 1 ? "" : "s"} saved without the document - attach it from the list`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} row${failCount === 1 ? "" : "s"} could not be submitted - see the rows marked in red`);
      return; // stay open so the user can fix and retry only those
    }
    onClose();
  };

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Top-up fuel by vehicle type</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-secondary py-2" style={{ fontSize: 13 }}>
          Pick a vehicle type and enter the top-up for each card - leave a row blank to skip it. Every amount becomes its
          own refill request for approval; the card balances move only once each one is approved. A vehicle with no fuel
          card gets a <b style={{ color: FUEL_ENTRY_COLORS.CASH.text }}>Cash</b> row - cash handed over for its fuel, not posted to Petty Cash.
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">Vehicle type <span className="text-danger">*</span></label>
            <VehicleTypeSelect value={vehicleType} onChange={setVehicleType} disabled={isSaving} />
            <small className="text-muted">{branchCode ? `Branch ${branchCode}` : "All my branches"}</small>
          </div>
          <div className="col-md-3">
            <label className="form-label">Refill date</label>
            <input type="date" className="form-control" value={refillDate} onChange={(e) => setRefillDate(e.target.value)} disabled={isSaving} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Reference no.</label>
            <input
              type="text"
              className="form-control"
              placeholder="bank / M-Pesa / provider reference - same on every refill, optional"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Remarks</label>
            <input
              type="text"
              className="form-control"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="optional - same on every refill"
              disabled={isSaving}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Supporting document</label>
            <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFile} disabled={isSaving} />
            <small className="text-muted">Optional - the approved top-up sheet. Attached to every refill created. JPEG, PNG or PDF, up to 2 MB.</small>
          </div>
        </div>

        {!vehicleType ? (
          <div className="text-muted text-center py-4">Select a vehicle type to list its fuel cards.</div>
        ) : loadingVehicles ? (
          <div className="text-muted text-center py-4">Loading {vehicleTypeLabel(vehicleType)} vehicles…</div>
        ) : rows.length === 0 ? (
          <div className="text-muted text-center py-4">
            No active {vehicleTypeLabel(vehicleType)} vehicle{branchCode ? ` in ${branchCode}` : ""}.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Card no</th>
                  <th>Vehicle</th>
                  <th className="text-end">Current balance</th>
                  <th style={{ width: 170 }} className="text-end">Top-up amount</th>
                  <th className="text-end">Balance after approval</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const id = r.key;
                  const isDone = Boolean(done[id]);
                  const amount = Number(amounts[id]);
                  const error = errors[id];
                  const rowClass = isDone ? "table-success" : error ? "table-danger" : "";
                  return (
                    <tr key={id} className={rowClass}>
                      <td className="text-muted">{i + 1}</td>
                      <td>
                        {r.mode === "CASH" ? (
                          <>
                            <PaymentModeBadge mode="CASH" />
                            <div className="small text-muted">no fuel card · {r.branchCode}</div>
                          </>
                        ) : (
                          <>
                            <span className="fw-semibold">{r.card.cardNumber}</span>
                            <div className="small text-muted">{r.card.provider} · {r.card.branchCode}</div>
                          </>
                        )}
                      </td>
                      <td>
                        {r.vehicles.map((v) => (
                          <div
                            key={v.regNo}
                            className={v.isDefault ? "fw-semibold" : "text-muted"}
                            title={`${v.brand} ${v.model}${v.isDefault ? " · default card for this vehicle" : " · not this vehicle's default card"}`}
                          >
                            {v.regNo}
                          </div>
                        ))}
                      </td>
                      <td className="text-end text-nowrap">{r.mode === "CASH" ? <span className="text-muted">-</span> : money(r.card.currentBalance)}</td>
                      <td>
                        {isDone ? (
                          <div className="text-end text-success small fw-semibold" title="Submitted in this session">{done[id]} ✓</div>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className={`form-control form-control-sm text-end${error ? " is-invalid" : ""}`}
                            value={amounts[id] ?? ""}
                            onChange={(e) => setAmount(id, e.target.value)}
                            disabled={isSaving}
                            placeholder="-"
                          />
                        )}
                        {error && <div className="small text-danger">{error}</div>}
                      </td>
                      <td className="text-end text-nowrap">
                        {isDone ? (
                          <span className="text-muted">pending approval</span>
                        ) : amount > 0 && r.mode === "CASH" ? (
                          <span className="small" style={{ color: FUEL_ENTRY_COLORS.CASH.text }}>cash {money(amount)}</span>
                        ) : amount > 0 ? (
                          <b>{money(Number(r.card.currentBalance || 0) + amount)}</b>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="fw-semibold">
                  <td colSpan={3}>
                    {pendingRows.length} of {rows.length} row{rows.length === 1 ? "" : "s"} to top up
                    {doneCount > 0 && <span className="text-success"> · {doneCount} submitted</span>}
                  </td>
                  <td className="text-end text-nowrap">{money(balanceTotal)}</td>
                  <td className="text-end text-nowrap">{money(pendingTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          {doneCount > 0 ? "Close" : "Cancel"}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {isSaving
            ? `Submitting ${pendingRows.length} refill${pendingRows.length === 1 ? "" : "s"}…`
            : `Submit ${pendingRows.length || ""} refill${pendingRows.length === 1 ? "" : "s"} for approval`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FuelRefillByTypeModal;
